import { apiRequest } from "../utils/useAPIRequest";
import constant from "../utils/constant";
import { adminStore, supportStore } from "../stores";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { queryClient } from "../App";
import { Value } from "sass";

const useGetBroadcast = (broadcastTableQuery, filterQuery, searchQuery) => {
  const query = useMemo(() => {
    if (searchQuery && filterQuery) {
      return `${broadcastTableQuery}&${filterQuery}&search=${searchQuery}`;
    } else if (searchQuery) {
      return `${broadcastTableQuery}&search=${searchQuery}`;
    } else if (filterQuery) {
      return `${broadcastTableQuery}&${filterQuery}`;
    } else {
      return broadcastTableQuery;
    }
  }, [filterQuery, broadcastTableQuery, searchQuery]);

  return useQuery({
    queryKey: ["getBroadcast", query],
    queryFn: () => {
      return apiRequest({
        url: `/notification/broadcasts?${query}`,
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};

const createBroadcast = async (payload, queryParams) => {
  const { status, errorMessage, data } = await apiRequest({
    url: `/notification/admin/broadcast?${queryParams}`,
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getBroadcast"],
    });
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};
const updateBroadcast = async (payload, queryParams, broadCastId) => {
  const { status, errorMessage, data } = await apiRequest({
    url: `/notification/update-broadcast/${broadCastId}?${queryParams}`,
    method: "patch",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getBroadcast"],
    });
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};

const deleteBroadcast = async (payload) => {
  const { status, errorMessage } = await apiRequest({
    url: "/notification/delete-one-broadcast",
    method: "patch",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getBroadcast"],
    });
    return { status };
  }
  return { status, errorMessage };
};

const getPlans = async () => {
  const { status, data, errorMessage } = await apiRequest({
    url: "/subscriptions/plans",
    method: "get",
  });

  if (status === constant.Success) {
    const plans = data.map((x) => ({
      value: x.name,
      key: x._id,
    }));
    adminStore.setPlans(plans);
    return { status: constant.Success };
  }

  return { errorMessage, status };
};
const getCompetitionParticipants = async (query) => {
  const { status, data, errorMessage } = await apiRequest({
    url: `/competition/participants?${query}`,
    method: "get",
  });

  if (status === constant.Success) {
    const participants = data?.participants?.map((x) => ({
      value: `${x.firstname} ${x.lastname}`,
      key: x._id,
    }));
    adminStore.setParticipants(participants);
    return { status: constant.Success };
  }

  return { errorMessage, status };
};
const getMimaPlans = async () => {
  const { status, data, errorMessage } = await apiRequest({
    url: "/subscriptions/plans?app=MIMA",
    method: "get",
  });

  if (status === constant.Success) {
    const plans = data.map((x) => ({
      value: `Mima ${x.name} ${x.region}`,
      key: x._id,
    }));
    adminStore.setMimaPlans(plans);
    return { status: constant.Success };
  }

  return { errorMessage, status };
};
const getFashPlans = async () => {
  const { status, data, errorMessage } = await apiRequest({
    url: "/subscriptions/plans?app=FASH",
    method: "get",
  });

  if (status === constant.Success) {
    const plans = data.map((x) => ({
      value: `Fash ${x.name} ${x.region}`,
      key: x._id,
    }));
    adminStore.setFashPlans(plans);
    return { status: constant.Success };
  }

  return { errorMessage, status };
};
const getTags = async () => {
  const { status, data, errorMessage } = await apiRequest({
    url: "/customers/prospect/tags",
    method: "get",
  });

  if (status === constant.Success) {
    adminStore.setTags(data);
    return { status: constant.Success };
  }

  return { errorMessage, status };
};
const getAllBusinesses = async () => {
  const query = `onlyParent=false&limit=20`;
  const { status, data, errorMessage } = await apiRequest({
    url: `/businesses?${query}`,
    method: "get",
  });

  if (status === constant.Success) {
    adminStore.setBusinessesList(data);
    return { status: constant.Success };
  }

  return { errorMessage, status };
};

export default {
  useGetBroadcast,
  createBroadcast,
  updateBroadcast,
  deleteBroadcast,
  getPlans,
  getAllBusinesses,
  getTags,
  getFashPlans,
  getMimaPlans,
  getCompetitionParticipants,
};
